/* Core & libraries */
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage, useIntl } from "react-intl";
import { ErrorMessage, Field, Formik } from "formik";
import { Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router";
import Toggle from "react-toggle";
import { PatternFormat } from "react-number-format";
import classnames from 'classnames';
import moment from "helpers/moment";

/* Helpers, constants, custom methods, internal libraries */
import {
  createValidationPolicyYupSchema,
  useValidationService
} from "../../../resources/validations/validations";
import { useGeneralService } from "../../../services/generalService";
import { useGuestProfileService } from "../../../services/guestProfileService";
import { useUserContext } from "../../../contexts/userContext";
import { PEOPLE_GUEST_ROUTE } from "../../../constants/routes.js";
import { moveByPriority } from "../../../helpers/general";

/* Components */
import UCButton from "../../buttons";
import UCSelect from "../../ucselect";
import { ReactComponent as UserDeleteIcon } from '../../../resources/images/user-x.svg';
import { ReactComponent as TrashRedIcon } from "../../../resources/images/trash-red-icon.svg";
import { ReactComponent as ASGuestCheck } from "../../../resources/images/as-guest-check.svg";
import { ReactComponent as ASGuestUser } from "../../../resources/images/as-guest-user.svg";
import { ReactComponent as ASGuestPassport } from "../../../resources/images/as-guest-passport.svg";
import { ReactComponent as ASGuestEmail } from "../../../resources/images/as-guest-email.svg";
import { ReactComponent as InfoIconSvg } from "../../../resources/images/info-icon.svg";
import UCPhoneCountrySelector from "../../ucPhoneCountrySelector";
import ASMembershipSelector from "../../asMembershipSelector";
import LabelDropdown from "../../label";
import GuestProfileFormSkeleton from "../skeleton";
import DeleteActionModal from "../../modals/deleteActionModal";

/* Styles */
import styles, { countrySelect } from "../styles.js";
import generalStyles from "../../../resources/styles/general";
import { colors } from "../../../constants/colors";

const GuestProfileForm = ({ guestId, guestDetails }) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const general = generalStyles({ theme });
  
  const formRef = useRef(null);
  const intl = useIntl();
  const { createGuestProfile, updateGuestProfile, deleteGuestProfile } = useGuestProfileService();
  const { getCountries } = useGeneralService();
  
  const navigate = useNavigate();
  const { guestProfileFormValidationObj } = useValidationService()
  const [validationSchema, setValidationSchema] = useState({});
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [countriesData, setCountriesData] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState(undefined);
  
  const userDetails = useUserContext();

  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  
  useEffect(() => {
    const result = createValidationPolicyYupSchema(guestProfileFormValidationObj);
    setValidationSchema(result)
  }, [])
  
  useEffect(() => {
    (async () => {
      const countriesResponse = await getCountries();
      setCountriesData(moveByPriority(countriesResponse, "value") || []);
    })();
  }, [])
  
  const submitForm = async (values, setSubmitting, resetForm) => {
    const trimmedDocNumber = values.documentNumber?.trim() || "";
    const originalDocNumber = guestDetails?.travelDocument?.documentNumber?.trim() || "";
    
    const travelDocumentPayload = trimmedDocNumber
      ? {
        nationality: values.nationality,
        countryOfResidence: values.countryOfResidence,
        documentNumber: trimmedDocNumber,
        expirationDate: values.expirationDate
          ? moment(values.expirationDate).format("YYYY-MM-DD")
          : "",
        ...(guestId && trimmedDocNumber !== originalDocNumber && {
          updateTravelDocument: true,
        }),
      }
      : null;
    
    const label = selectedLabel === undefined ? guestDetails?.label : selectedLabel;
    const payload = {
      userEntityId: guestId || "",
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      suffix: values.suffix,
      dateOfBirth: moment(values.dateOfBirth).format("YYYY-MM-DD"),
      gender: values.gender,
      preferredName: values.preferredName,
      emailAddress: values.emailAddress,
      phone: {
        countryCode: values.phoneCountryCode,
        number: values.phoneNumber,
      },
      travelerInfo: {
        knownTravelerNumber: values.knownTravelerNumber,
        redressNumber: values.redressNumber,
      },
      membershipProgram: {
        membershipNumber: values.membershipNumber,
        airline: values.airline,
      },
      ...(label && {
        label: {
          labelId: label.labelId,
        }
      })
    };
    
    if (guestId) {
      payload.travelDocument = travelDocumentPayload;
      await updateGuestProfile(payload);
    } else {
      payload.travelDocument = travelDocumentPayload;
      const response = await createGuestProfile(payload);
      if (response) {
        if (values?.addAnotherGuest) {
          resetForm();
          setSelectedLabel(null);
        } else {
          navigate(PEOPLE_GUEST_ROUTE);
        }
        window.scrollTo(0, 0);
      }
    }
    
    setSubmitting(false);
  }
  
  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }

  const cancelUpdate = () => {
    if (formRef.current) {
      formRef.current.resetForm()
    }
    navigate(PEOPLE_GUEST_ROUTE);
    window.scrollTo(0, 0);
  }
  
  const genders = [
    { value: 'M', label: <FormattedMessage id="guest.genderMale" /> },
    { value: 'F', label: <FormattedMessage id="guest.genderFemale" /> },
    { value: 'X', label: <FormattedMessage id="guest.genderUnspecified" /> },
    { value: 'U', label: <FormattedMessage id="guest.genderUndisclosed" /> },
  ];
  
  const handleDelete = async () => {
    const response = await deleteGuestProfile(guestId);
    if (response) {
      setShowDeleteModal(false);
      navigate(PEOPLE_GUEST_ROUTE);
      window.scrollTo(0, 0);
    }
  }
  
  const validate = (values) => {
    const errors = {};
    
    const isMaskedPassport = /^\*+/.test(values.documentNumber);
    if (values.documentNumber && !isMaskedPassport) {
      if (!/^[A-Z0-9]+$/i.test(values.documentNumber)) {
        errors.documentNumber = <FormattedMessage id="guest.passportMatches" />;
      } else if (values.documentNumber.length < 6) {
        errors.documentNumber = <FormattedMessage id="guest.passportMin" />;
      } else if (values.documentNumber.length > 12) {
        errors.documentNumber = <FormattedMessage id="guest.passportMax" />;
      }
    }
    
    if (values.documentNumber) {
      if (!values.nationality) {
        errors.nationality = <FormattedMessage id="guest.nationalityRequired" />;
      }
      if (!values.countryOfResidence) {
        errors.countryOfResidence = <FormattedMessage id="guest.countryOfResidenceRequired" />;
      }
      if (!values.expirationDate) {
        errors.expirationDate = <FormattedMessage id="guest.expirationDateRequired" />;
      } else {
        const expiry = moment(values.expirationDate, 'MM/DD/YYYY', true);
        if (!expiry.isValid() || moment().isAfter(expiry, 'day')) {
          errors.expirationDate = <FormattedMessage id="guest.expiresPast" />;
        }
      }
    }
    
    if (values.dateOfBirth) {
      const dob = moment(values.dateOfBirth, 'MM/DD/YYYY', true);
      if (!dob.isValid() || moment().isSameOrBefore(dob, 'day')) {
        errors.dateOfBirth = <FormattedMessage id="guest.dobAfter" />;
      }
    }
    
    return errors;
  };
  
  return (
    <>
      <div className={classes.dividerContainer}>
        <hr className={classes.border}/>
      </div>
      { (guestId && guestDetails) || !guestId ?
        <>
          <div className={classes.details}>
            <div>
              <div className={classes.title}>
                <FormattedMessage id="guest.formTitle"/>
              </div>
              <div className={classes.description}>
                <FormattedMessage id="guest.fromSubtitle"/>
              </div>
            </div>
            <div className={classes.btnContainer}>
              <UCButton
                type="default"
                className={classes.buttonCancel}
                onClick={cancelUpdate}
                text={<FormattedMessage id="guest.cancel"/>}
              />
              <UCButton
                type="primary"
                className={classes.button}
                onClick={handleFormSubmit}
                text={<FormattedMessage id="guest.save"/>}
                spinner={{
                  name: "guestProfilePage",
                  size: 20,
                  color: colors.white,
                }}
              />
            </div>
          </div>
          
          <div className={classes.dividerContainer}>
            <hr className={classes.border}/>
          </div>
          
          <div className={classes.editCompany}>
            <Formik
              innerRef={formRef}
              enableReinitialize={true}
              initialValues={{
                firstName: guestDetails?.firstName || "",
                lastName: guestDetails?.lastName || "",
                middleName: guestDetails?.middleName || "",
                suffix: guestDetails?.suffix || "",
                dateOfBirth: guestDetails && moment(guestDetails?.dateOfBirth).format("MM/DD/YYYY") || "",
                gender: guestDetails?.gender || "",
                preferredName: guestDetails?.preferredName || "",
                emailAddress: guestDetails?.emailAddress || "",
                phoneCountryCode: guestDetails?.phone?.countryCode || "US",
                phoneNumber: guestDetails?.phone?.number || "",
                knownTravelerNumber: guestDetails?.travelerInfo?.knownTravelerNumber || "",
                redressNumber: guestDetails?.travelerInfo?.redressNumber || "",
                membershipNumber: guestDetails?.membershipProgram?.membershipNumber || "",
                airline: guestDetails?.membershipProgram?.airline || "AS",
                nationality: guestDetails?.travelDocument?.nationality || "",
                countryOfResidence: guestDetails?.travelDocument?.countryOfResidence || "",
                documentNumber: guestDetails?.travelDocument?.documentNumber || "",
                expirationDate: guestDetails && guestDetails?.travelDocument?.expirationDate &&
                  moment(guestDetails?.travelDocument?.expirationDate).format("MM/DD/YYYY") || "",
                addAnotherGuest: false,
              }}
              validationSchema={validationSchema}
              validate={validate}
              onSubmit={(values, {setSubmitting, resetForm}) => {
                submitForm(values, setSubmitting, resetForm);
              }}
            >
              {({
                  values,
                  errors,
                  touched,
                  setFieldTouched,
                  handleSubmit,
                  setFieldValue,
                }) => (
                <form onSubmit={handleSubmit}>
                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className={classes.location}>
                          <FormattedMessage id="guest.identityDetails"/>
                          <span className={classes.requiredAsterisk}>*</span>
                        </div>
                      </div>
                      <div className="col-12 col-md-8">
                        <div className={classes.formContainer}>
                          <div className={classes.inputContainer}>
                            <label htmlFor="firstName" className={classes.label}>
                              <FormattedMessage id="guest.firstName"/>
                           </label>
                            <Field
                              placeholder={intl.formatMessage({id: 'guest.firstName'})}
                              type="text"
                              name="firstName"
                              id="firstName"
                              className={
                                errors.firstName && touched.firstName
                                  ? "input-error"
                                  : null
                              }
                            />
                            <ErrorMessage
                              name="firstName"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="middleName" className={classes.label}>
                              <FormattedMessage id="guest.middleName"/>
                           </label>
                            <Field
                              placeholder={intl.formatMessage({id: 'guest.middleName'})}
                              type="text"
                              name="middleName"
                              id="middleName"
                              className={
                                errors.middleName && touched.middleName
                                  ? "input-error"
                                  : null
                              }
                            />
                            <ErrorMessage
                              name="middleName"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                          <div className="row">
                            <div className="col-xl-7 col-12">
                              <div className={classes.inputContainer}>
                                <label htmlFor="lastName" className={classes.label}>
                                  <FormattedMessage id="guest.lastName"/>
                               </label>
                                <Field
                                  placeholder={intl.formatMessage({id: 'guest.lastName'})}
                                  type="text"
                                  name="lastName"
                                  id="lastName"
                                  className={
                                    errors.lastName && touched.lastName
                                      ? "input-error"
                                      : null
                                  }
                                />
                                <ErrorMessage
                                  name="lastName"
                                  component="span"
                                  className={classes.error}
                                />
                              </div>
                            </div>
                            <div className="col-xl-5 col-12">
                              <div className={classes.inputContainer}>
                                <label htmlFor="suffix" className={classes.label}>
                                  <FormattedMessage id="guest.suffix"/>
                               </label>
                                <Field
                                  placeholder={intl.formatMessage({id: 'guest.suffix'})}
                                  type="text"
                                  name="suffix"
                                  id="suffix"
                                  className={
                                    errors.suffix && touched.suffix
                                      ? "input-error"
                                      : null
                                  }
                                />
                                <ErrorMessage
                                  name="suffix"
                                  component="span"
                                  className={classes.error}
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="row">
                            <div className="col-xl-7 col-12">
                              <div className={classes.inputContainer}>
                                <label htmlFor="dateOfBirth" className={classes.label}>
                                  <FormattedMessage id="guest.dateOfBirth"/>
                               </label>
                                <Field
                                  name="dateOfBirth"
                                  render={({form}) => (
                                    <PatternFormat
                                      name="dateOfBirth"
                                      format="##/##/####"
                                      placeholder="MM/DD/YYYY"
                                      mask="_"
                                      value={values.dateOfBirth}
                                      className={
                                        errors.dateOfBirth && touched.dateOfBirth
                                          ? "input-error"
                                          : null
                                      }
                                      onBlur={() => {
                                        form.setFieldTouched("dateOfBirth", true)
                                      }}
                                      onValueChange={(values) => {
                                        form.setFieldValue("dateOfBirth", values.formattedValue);
                                      }}
                                    />
                                  )}
                                />
                                <ErrorMessage
                                  name="dateOfBirth"
                                  component="span"
                                  className={classes.error}
                                />
                              </div>
                            </div>
                            <div className="col-xl-5 col-12">
                              <div className={classes.inputContainer}>
                                <label htmlFor="gender" className={classes.label}>
                                  <FormattedMessage id="guest.gender"/>
                               </label>
                                <Field
                                  type="select"
                                  name="gender"
                                  id="gender"
                                  render={({form}) => (
                                    <div>
                                      <UCSelect
                                        className={
                                          errors.gender && touched.gender
                                            ? "input-error select"
                                            : null
                                        }
                                        displayName={<FormattedMessage id="guest.gender"/>}
                                        value={values.gender}
                                        options={genders}
                                        onBlur={() => setFieldTouched("gender", true)}
                                        defaultValue={genders.find(item => item.value === values.state)}
                                        onChange={(item) => form.setFieldValue("gender", item.value)}
                                        isSearchable={true}
                                      />
                                      <ErrorMessage
                                        name="gender"
                                        component="span"
                                        className={classes.error}
                                      />
                                    </div>
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="preferredName" className={classes.label}>
                              <FormattedMessage id="guest.preferredName"/>
                           </label>
                            <Field
                              placeholder={intl.formatMessage({id: 'guest.preferredName'})}
                              type="text"
                              name="preferredName"
                              id="preferredName"
                              className={
                                errors.preferredName && touched.preferredName
                                  ? "input-error"
                                  : null
                              }
                            />
                            <ErrorMessage
                              name="preferredName"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={classes.dividerContainer}>
                    <hr className={classes.border}/>
                  </div>
                  
                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className={classes.location}>
                          <FormattedMessage id="guest.phone"/>
                        </div>
                      </div>
                      <div className="col-12 col-md-8">
                        <div className={classes.formContainer}>
                          <div className={classes.phoneInputContainer}>
                            <label htmlFor="phoneNumber" className={classes.label}>
                              <FormattedMessage id="guest.phone"/>
                           </label>
                            <UCPhoneCountrySelector
                              providerCode={providerCode}
                              errors={errors}
                              touched={touched}
                              values={values}
                            />
                            <ErrorMessage
                              name="phoneNumber"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className={classes.dividerContainer}>
                      <hr className={classes.border}/>
                    </div>
                    
                    <div className={classes.formCont}>
                      <div className="row">
                        <div className="col-12 col-md-4">
                          <div className={classes.location}>
                            <FormattedMessage id="guest.travelerInfo"/>
                          </div>
                        </div>
                        <div className="col-12 col-md-8">
                          <div className={classes.formContainer}>
                            <div className={classes.inputContainerWithSvg}>
                              <label htmlFor="knownTravelerNumber" className={classes.knownTravelerLabel}>
                                <FormattedMessage id="guest.knownTravelerNumber"/>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip
                                      className={`${general.main} ${classes.tooltip}`}
                                    >
                                      <div className={classes.infoDescription}>
                                        <FormattedMessage
                                          id="guest.knownTraveler.tooltip"
                                        />
                                      </div>
                                    </Tooltip>
                                  }
                                >
                                  <InfoIconSvg className={classes.infoIcon}/>
                                </OverlayTrigger>
                             </label>
                              <div
                                className={errors.knownTravelerNumber && touched.knownTravelerNumber ? "input-error" : null}
                              >
                                <ASGuestUser/>
                                <Field
                                  placeholder={intl.formatMessage({id: 'guest.knownTravelerNumber'})}
                                  type="text"
                                  name="knownTravelerNumber"
                                  id="knownTravelerNumber"
                                />
                              </div>
                              <ErrorMessage
                                name="knownTravelerNumber"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                            <div className={classes.inputContainerWithSvg}>
                              <label htmlFor="redressNumber" className={classes.label}>
                                <FormattedMessage id="guest.redressNumber"/>
                             </label>
                              <div
                                className={errors.redressNumber && touched.redressNumber ? "input-error" : null}
                              >
                                <ASGuestCheck/>
                                <Field
                                  placeholder={intl.formatMessage({id: 'guest.redressNumber'})}
                                  type="text"
                                  name="redressNumber"
                                  id="redressNumber"
                                />
                              </div>
                              <ErrorMessage
                                name="redressNumber"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                            <div className={classes.inputContainerWithSvg}>
                              <label htmlFor="emailAddress" className={classes.label}>
                                <FormattedMessage id="guest.emailAddress"/>
                             </label>
                              <div
                                className={errors.emailAddress && touched.emailAddress ? "input-error" : null}
                              >
                                <ASGuestEmail/>
                                <Field
                                  placeholder={intl.formatMessage({id: 'guest.emailAddress'})}
                                  type="text"
                                  name="emailAddress"
                                  id="emailAddress"
                                
                                />
                              </div>
                              <ErrorMessage
                                name="emailAddress"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                            <div className={classes.customInputContainer}>
                              <label htmlFor="membershipNumber" className={classes.label}>
                                <FormattedMessage id="guest.mileageProgram"/>
                             </label>
                              <ASMembershipSelector
                                fieldName="membershipNumber"
                                selectFieldName="airline"
                                providerCode={providerCode}
                                errors={errors}
                                touched={touched}
                                values={values}
                              />
                              <ErrorMessage
                                name="membershipNumber"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={classes.dividerContainer}>
                      <hr className={classes.border}/>
                    </div>
                    <div className={classes.formCont}>
                      <div className="row">
                        <div className="col-12 col-md-4">
                          <div className={classes.location}>
                            <FormattedMessage id="guest.passportDetails"/>
                          </div>
                        </div>
                        <div className="col-12 col-md-8">
                          <div className={classes.formContainer}>
                            <div className={classes.inputContainer}>
                              <label htmlFor="nationality" className={classes.label}>
                                <FormattedMessage id="guest.countryOfCitizenship"/>
                             </label>
                              <Field
                                type="select"
                                name="nationality"
                                id="nationality"
                                render={({form}) => (
                                  <div>
                                    <UCSelect
                                      className={
                                        errors.nationality && touched.nationality
                                          ? "input-error select"
                                          : null
                                      }
                                      styles={countrySelect}
                                      displayName={<FormattedMessage id="guest.countryOfCitizenship"/>}
                                      value={values.nationality}
                                      options={countriesData}
                                      onBlur={() => setFieldTouched("nationality", true)}
                                      defaultValue={countriesData.find(item => item.value === values.nationality)}
                                      onChange={(item) => form.setFieldValue("nationality", item.value)}
                                      isSearchable={true}
                                    />
                                    <ErrorMessage
                                      name="nationality"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </div>
                                )}
                              />
                            </div>
                            <div className={classes.inputContainer}>
                              <label htmlFor="countryOfResidence" className={classes.label}>
                                <FormattedMessage id="guest.countryOfResidence"/>
                             </label>
                              <Field
                                type="select"
                                name="countryOfResidence"
                                id="countryOfResidence"
                                render={({form}) => (
                                  <div>
                                    <UCSelect
                                      className={
                                        errors.countryOfResidence && touched.countryOfResidence
                                          ? "input-error select"
                                          : null
                                      }
                                      styles={countrySelect}
                                      displayName={<FormattedMessage id="guest.countryOfResidence"/>}
                                      value={values.countryOfResidence}
                                      options={countriesData}
                                      onBlur={() => setFieldTouched("countryOfResidence", true)}
                                      defaultValue={countriesData.find(item => item.value === values.countryOfResidence)}
                                      onChange={(item) => form.setFieldValue("countryOfResidence", item.value)}
                                      isSearchable={true}
                                    />
                                    <ErrorMessage
                                      name="countryOfResidence"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </div>
                                )}
                              />
                            </div>
                            <div className={classes.inputContainerWithSvg}>
                              <label htmlFor="documentNumber" className={classes.label}>
                                <FormattedMessage id="guest.passportNumber"/>
                             </label>
                              <div
                                className={errors.documentNumber && touched.documentNumber ? "input-error" : null}
                              >
                                <ASGuestPassport/>
                                <Field
                                  placeholder={intl.formatMessage({id: 'guest.passportNumber'})}
                                  type="text"
                                  name="documentNumber"
                                  id="documentNumber"
                                />
                              </div>
                              <ErrorMessage
                                name="documentNumber"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                            <div className={classnames(classes.inputContainer, classes.expire)}>
                              <label htmlFor="expirationDate" className={classes.label}>
                                <FormattedMessage id="guest.expires"/>
                             </label>
                              <Field
                                name="expirationDate"
                                render={({form}) => (
                                  <PatternFormat
                                    name="expirationDate"
                                    format="##/##/####"
                                    placeholder="MM/DD/YYYY"
                                    mask="_"
                                    value={values.expirationDate}
                                    className={
                                      errors.expirationDate && touched.expirationDate
                                        ? "input-error"
                                        : null
                                    }
                                    onBlur={() => {
                                      form.setFieldTouched("expirationDate", true)
                                    }}
                                    onValueChange={(values) => {
                                      form.setFieldValue("expirationDate", values.formattedValue);
                                    }}
                                  />
                                )}
                              />
                              <ErrorMessage
                                name="expirationDate"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className={classes.dividerContainer}>
                      <hr className={classes.border}/>
                    </div>
                    
                    <div className={classes.formCont}>
                      <div className="row">
                        <div className="col-12 col-md-4">
                          <div className={classes.location}>
                            <FormattedMessage id="guest.label"/>
                          </div>
                        </div>
                        <div className="col-12 col-md-8">
                          <LabelDropdown
                            activeLabel={guestDetails?.label || selectedLabel}
                            setSelectedLabelCallback={setSelectedLabel}
                            placement="top-start"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={classes.dividerContainer}>
                    <hr className={classes.border}/>
                  </div>
                  
                  <Row className={classes.formFooter}>
                    <Col xs={6}>
                      {guestId ?
                        <UCButton
                          type="deleteLink"
                          icon={<TrashRedIcon/>}
                          className={classes.deleteCompanyButton}
                          text={<FormattedMessage id="guest.deleteProfile"/>}
                          onClick={() => setShowDeleteModal(true)}
                        /> :
                        <div className={`${classes.toggle} ${classes.toggleContainer}`}>
                          <Toggle
                            icons={null}
                            onChange={(e) => {
                              setFieldValue("addAnotherGuest", e.target.checked);
                            }}
                            checked={values.addAnotherGuest}
                          />
                          <span><FormattedMessage id="guest.addAnotherGuest"/></span>
                        </div>
                      }
                    </Col>
                    <Col xs={6}>
                      <div className={classes.buttonContainer}>
                        <div className={classes.btnContainer}>
                          <UCButton
                            type="default"
                            className={classes.buttonCancel}
                            onClick={cancelUpdate}
                            text={<FormattedMessage id="guest.cancel"/>}
                          />
                          <UCButton
                            type="primary"
                            className={classes.button}
                            onClick={handleFormSubmit}
                            text={<FormattedMessage id="guest.save"/>}
                            spinner={{
                              name: "guestProfilePage",
                              size: 20,
                              color: colors.white
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </form>
              )}
            </Formik>
            
            {guestId &&
              <DeleteActionModal
                showModal={showDeleteModal}
                setShowModal={setShowDeleteModal}
                setSelectedRow={() => {
                }}
                modalIcon={<UserDeleteIcon/>}
                modalTitle={<FormattedMessage id="guest.delete.title"/>}
                modalDescription={<FormattedMessage id="guest.delete.description"/>}
                submitCallback={handleDelete}
              />
            }
          </div>
        </> : <GuestProfileFormSkeleton/>
      }
    </>
  )
}


export default GuestProfileForm;
